
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  MessageSquare, 
  Search, 
  Plus, 
  AlertCircle, 
  CheckCircle,
  Clock,
  Filter,
  RefreshCcw
} from 'lucide-react';

const HQSupport = () => {
  const tickets = [
    {
      id: 1,
      title: "Dallas studio unable to access scheduling system",
      description: "Staff reporting error when trying to book new appointments",
      studio: "Dallas Downtown",
      status: "open",
      priority: "high",
      createdAt: "2024-02-10T09:30:00",
      assignedTo: "Michael Chen"
    },
    {
      id: 2,
      title: "Issue with credit redemption in Houston",
      description: "Credits not being properly deducted when used for payment",
      studio: "Houston Medical",
      status: "in-progress",
      priority: "medium",
      createdAt: "2024-02-09T14:15:00",
      assignedTo: "Sarah Johnson"
    },
    {
      id: 3,
      title: "Request for additional training materials",
      description: "Need more video content for new staff onboarding",
      studio: "Austin West",
      status: "open",
      priority: "low",
      createdAt: "2024-02-08T11:45:00",
      assignedTo: "Unassigned"
    },
    {
      id: 4,
      title: "Marketing materials showing incorrect prices",
      description: "Promotional flyers have outdated pricing information",
      studio: "Dallas Downtown",
      status: "resolved",
      priority: "medium",
      createdAt: "2024-02-07T15:20:00",
      assignedTo: "Emily Rodriguez"
    }
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-500/20 text-red-200 border-red-500/30';
      case 'medium': return 'bg-amber-500/20 text-amber-200 border-amber-500/30';
      case 'low': return 'bg-green-500/20 text-green-200 border-green-500/30';
      default: return 'bg-slate-500/20 text-slate-200 border-slate-500/30';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-blue-500/20 text-blue-200 border-blue-500/30';
      case 'in-progress': return 'bg-amber-500/20 text-amber-200 border-amber-500/30';
      case 'resolved': return 'bg-emerald-500/20 text-emerald-200 border-emerald-500/30';
      case 'closed': return 'bg-slate-500/20 text-slate-200 border-slate-500/30';
      default: return 'bg-slate-500/20 text-slate-200 border-slate-500/30';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open': 
        return <AlertCircle className="w-4 h-4" />;
      case 'in-progress': 
        return <Clock className="w-4 h-4" />;
      case 'resolved': 
        return <CheckCircle className="w-4 h-4" />;
      case 'closed': 
        return <CheckCircle className="w-4 h-4" />;
      default: 
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-amber-900">
      {/* Header */}
      <div className="bg-slate-800/50 backdrop-blur border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-white">
                Support Center
              </h1>
              <p className="mt-1 text-amber-200">
                Internal support requests and issue tracking
              </p>
            </div>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white">
              <Plus className="w-4 h-4 mr-2" />
              New Ticket
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Search and Filters */}
          <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Search tickets..."
                    className="pl-10 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400"
                  />
                </div>
                <div className="flex space-x-3">
                  <Button variant="outline" className="border-slate-600 text-slate-300">
                    <Filter className="w-4 h-4 mr-2" />
                    Status
                  </Button>
                  <Button variant="outline" className="border-slate-600 text-slate-300">
                    <Filter className="w-4 h-4 mr-2" />
                    Priority
                  </Button>
                  <Button variant="outline" className="border-slate-600 text-slate-300">
                    <Filter className="w-4 h-4 mr-2" />
                    Studio
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ticket Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="bg-slate-800/50 backdrop-blur border-blue-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-blue-500/20">
                    <AlertCircle className="h-6 w-6 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-200">Open</p>
                    <p className="text-2xl font-bold text-white">2</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-amber-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-amber-500/20">
                    <Clock className="h-6 w-6 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm text-amber-200">In Progress</p>
                    <p className="text-2xl font-bold text-white">1</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-emerald-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-emerald-500/20">
                    <CheckCircle className="h-6 w-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm text-emerald-200">Resolved</p>
                    <p className="text-2xl font-bold text-white">1</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-red-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-red-500/20">
                    <RefreshCcw className="h-6 w-6 text-red-400" />
                  </div>
                  <div>
                    <p className="text-sm text-red-200">Avg. Resolution</p>
                    <p className="text-2xl font-bold text-white">8h</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Ticket List */}
          <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
            <CardHeader>
              <CardTitle className="text-white">Support Tickets</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {tickets.map((ticket) => (
                  <div key={ticket.id} className="p-4 bg-slate-700/30 rounded-lg border border-slate-600/30">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-white font-semibold">{ticket.title}</h3>
                          <Badge className={getPriorityColor(ticket.priority)}>
                            {ticket.priority}
                          </Badge>
                          <Badge className={getStatusColor(ticket.status)}>
                            <span className="flex items-center">
                              {getStatusIcon(ticket.status)}
                              <span className="ml-1">{ticket.status}</span>
                            </span>
                          </Badge>
                        </div>
                        <p className="text-slate-300 text-sm mb-3">{ticket.description}</p>
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center space-x-4 text-slate-400">
                            <span>Studio: {ticket.studio}</span>
                            <span>Assigned: {ticket.assignedTo}</span>
                            <span>Created: {new Date(ticket.createdAt).toLocaleDateString()}</span>
                          </div>
                          <div className="flex space-x-2">
                            <Button size="sm" variant="outline" className="border-slate-600 text-slate-300">
                              View
                            </Button>
                            {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
                              <Button size="sm" className="bg-amber-600 hover:bg-amber-700">
                                Update
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default HQSupport;
